import type { BaseConditionContext, ConditionMap } from '../conditions';
import { evaluateAnyCondition } from '../conditions';
import type { CheckRouteGuardParams, RouteGuardResult } from './types';

/**
 * Checks if a route is allowed based on the route guard configuration.
 *
 * This is pure logic - it evaluates conditions and returns a result object.
 * The calling app is responsible for:
 * - Generating the actual redirect URL (SSO or login)
 * - Performing the redirect
 *
 * @param params - The route guard check parameters
 * @returns RouteGuardResult indicating whether the route is allowed, or redirect info if not
 *
 * @example
 * const result = checkRouteGuard({
 *     config: elektraRouteGuard,
 *     context: conditionContext,
 *     conditions: elektraConditions,
 * });
 *
 * if (!result.allowed) {
 *     if (result.isAuthenticated) {
 *         // Use SSO to redirect to target domain
 *         const url = generateSSOUrl({
 *             targetDomain: result.targetDomain,
 *             redirectPath: result.fallbackRoute,
 *         });
 *         window.location.href = url;
 *     } else {
 *         // Redirect to login
 *         window.location.href = getLoginUrl(result.fallbackRoute);
 *     }
 * }
 */
export function checkRouteGuard<TContext extends BaseConditionContext>(
    params: CheckRouteGuardParams<TContext>,
): RouteGuardResult {
    const { config, context, conditions } = params;

    // Check if any of the allowed conditions pass
    const isAllowed = evaluateAnyCondition(
        config.allowedConditions,
        context,
        conditions,
    );

    if (isAllowed) {
        return { allowed: true };
    }

    return {
        allowed: false,
        isAuthenticated: context.isAuthenticated,
        fallbackRoute: config.getFallbackRoute(context),
        targetDomain: config.targetDomain ?? 'portaal',
    };
}

/**
 * Convenience function to check if a specific path is allowed.
 * Creates a temporary context with the given path and checks the route guard.
 *
 * @param path - The path to check
 * @param baseContext - The base context (without currentPath)
 * @param config - The route guard configuration
 * @param conditions - Map of named conditions
 * @returns true if the path is allowed, false otherwise
 *
 * @example
 * const canAccess = isPathAllowed(
 *     '/projects/123',
 *     { currentUser, isAuthenticated: true },
 *     elektraRouteGuard,
 *     elektraConditions,
 * );
 */
export function isPathAllowed<TContext extends BaseConditionContext>(
    path: string,
    baseContext: Omit<TContext, 'currentPath'>,
    config: Pick<CheckRouteGuardParams<TContext>['config'], 'allowedConditions'>,
    conditions: ConditionMap<TContext>,
): boolean {
    const context = { ...baseContext, currentPath: path } as TContext;

    return evaluateAnyCondition(
        config.allowedConditions,
        context,
        conditions,
    );
}
